import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  SPOTIFY_AUTHORIZE_PATH,
  disconnectSpotify,
  spotifyStatus,
  type SpotifyStatus,
} from "../services/http.js";
import { serverUrl } from "../services/serverOrigin.js";

/**
 * Connect a Spotify account — phase 19c, ADR 0021.
 *
 * # Renders nothing when the installation does not use Spotify
 *
 * Not a disabled button and not an explanation: an installation that is not
 * configured for it has no such feature, and a greyed-out control is a promise
 * that something is coming.
 *
 * # Why the connect action is a navigation and not a fetch
 *
 * `GET /api/spotify/authorize` answers a 303 to Spotify's consent page.
 * `fetch` would follow that redirect in the background, get an HTML page, and
 * throw it away — the person never sees the consent screen. The browser has to
 * *go* there.
 *
 * ⚠️ `serverUrl` rather than a path, because `/display` is not the only thing
 * that can be packaged and nothing in this codebase builds a URL out of
 * `window.location` (see `frontend.md`).
 */
export function SpotifyPanel() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<SpotifyStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    void spotifyStatus().then(setStatus, () => {
      // A failure here is not worth an error line: the panel simply does not
      // appear, which is the same as an installation that has no Spotify.
      setStatus(null);
    });
  }, []);

  useEffect(load, [load]);

  // Three-state loading: `null` is "still asking", and an unconfigured
  // installation renders nothing at all.
  if (!status || !status.configured) {
    return null;
  }

  const outcome = new URLSearchParams(window.location.search).get("spotify");

  return (
    <section
      aria-label={t("settings.spotify.label")}
      className="flex flex-col gap-3 rounded-xl border border-white/10 bg-stage-900/60 p-5 backdrop-blur"
    >
      <h2 className="text-sm font-semibold">{t("settings.spotify.heading")}</h2>

      {outcome === "not_allowlisted" && (
        // The failure nothing about the browser explains: a development-mode
        // app admits five people, added by hand in the dashboard.
        <p className="text-sm text-amber-300/90">
          {t("settings.spotify.notAllowlisted")}
        </p>
      )}
      {outcome === "failed" && (
        <p className="text-sm text-wrong-500">{t("settings.spotify.failed")}</p>
      )}

      {status.connected ? (
        <>
          <p className="text-sm text-white/70">
            {t("settings.spotify.connectedAs", {
              name: status.display_name ?? t("settings.spotify.unnamed"),
            })}
          </p>
          {!status.scopes_current && (
            // Surfaced rather than discovered: the alternative is a 403 from a
            // playlist read that nobody can interpret.
            <p className="text-sm text-amber-300/90">
              {t("settings.spotify.reconnectNeeded")}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {!status.scopes_current && (
              <a
                href={serverUrl(SPOTIFY_AUTHORIZE_PATH)}
                className="rounded-md bg-flash-500/20 px-3 py-1.5 text-sm text-flash-300"
              >
                {t("settings.spotify.reconnect")}
              </a>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void disconnectSpotify().then(
                  () => {
                    setBusy(false);
                    load();
                  },
                  () => {
                    setBusy(false);
                  },
                );
              }}
              className="rounded-md bg-white/10 px-3 py-1.5 text-sm text-white/70 disabled:opacity-50"
            >
              {t("settings.spotify.disconnect")}
            </button>
          </div>
          {/* ⚠️ sonolume cannot revoke the grant itself — said plainly rather
              than implied by a button called "disconnect". */}
          <p className="text-xs text-white/40">{t("settings.spotify.revokeNote")}</p>
        </>
      ) : (
        <>
          <p className="text-sm text-white/60">{t("settings.spotify.why")}</p>
          <a
            href={serverUrl(SPOTIFY_AUTHORIZE_PATH)}
            className="self-start rounded-md bg-flash-500/20 px-3 py-1.5 text-sm text-flash-300"
          >
            {t("settings.spotify.connect")}
          </a>
        </>
      )}
    </section>
  );
}
